/**
 * FactCard + InfoRow — the story page's grouped-facts primitives (uispec
 * §Overview / FactCardGrid). A FactCard is an icon + title Card that holds
 * a column of InfoRows; the consuming module fills one FactCard per theme
 * ("Customer Information", "Invoice Details", and one per custom-field
 * group_name).
 *
 * InfoRow: label left (muted `--color-text-soft`, min-w ~140px) / value
 * right, separated by a hairline divider; the last row drops its divider.
 * Presentational only — the module resolves canonical-vs-custom values
 * before passing them in.
 */

import {
  Children,
  cloneElement,
  Fragment,
  forwardRef,
  isValidElement,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from "react";

import { cn } from "../lib/utils";
import { Card } from "./card";

export interface InfoRowProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  /** Rendered value; falsy (null/undefined/"") shows an em-dash. */
  value?: ReactNode;
}

/**
 * The one "this field is empty" rule — InfoRow draws "—" for it, and FactCard
 * folds a row holding it (POP-02, ADR-0230). `0` is a value, never empty.
 */
export function isEmptyFieldValue(value: unknown): boolean {
  return (
    value === null || value === undefined || value === "" || value === false
  );
}

export const InfoRow = forwardRef<HTMLDivElement, InfoRowProps>(
  ({ label, value, className, ...props }, ref) => {
    const empty = isEmptyFieldValue(value);
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-start justify-between gap-4 py-3",
          "border-b border-[var(--color-border-hairline)] last:border-0",
          className,
        )}
        {...props}
      >
        <span className="text-sm font-medium text-[var(--color-text-soft)] min-w-[140px]">
          {label}
        </span>
        <span className="text-sm text-right text-[var(--color-text-strong)]">
          {empty ? "—" : value}
        </span>
      </div>
    );
  },
);
InfoRow.displayName = "InfoRow";

export interface FactCardProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  /** Leading icon (lucide element). Decorative — rendered aria-hidden. */
  icon?: ReactNode;
  children: ReactNode;
}

/**
 * The card's children as a flat list: fragments opened, nulls and `false`
 * from conditional rows dropped, mapped arrays spread — so a row is judged
 * the same whether it was written inline, behind a condition, or in a map.
 */
function flattenChildren(children: ReactNode): ReactNode[] {
  const out: ReactNode[] = [];
  Children.toArray(children).forEach((child) => {
    if (isValidElement(child) && child.type === Fragment) {
      const inner = (child.props as { children?: ReactNode }).children;
      flattenChildren(inner).forEach((c, i) => {
        // Keep keys unique across the flattened list.
        out.push(
          isValidElement(c)
            ? cloneElement(c, { key: `${String(child.key)}${String(c.key ?? i)}` })
            : c,
        );
      });
      return;
    }
    out.push(child);
  });
  return out;
}

/** True for an `InfoRow` whose value is empty — the rows a card folds. */
function isEmptyInfoRow(child: ReactNode): boolean {
  return (
    isValidElement(child) &&
    child.type === InfoRow &&
    isEmptyFieldValue((child.props as InfoRowProps).value)
  );
}

/**
 * FactCard folds its empty fields (POP-02, owner ruling D-108, ADR-0230): the
 * filled rows show; the `InfoRow`s whose value is empty (the same rule InfoRow
 * draws "—" for) wait behind a "Show N empty field(s)" button that unfolds
 * them at the foot of the card and turns into "Hide N empty field(s)". No
 * button when nothing is empty. Anything that is not an `InfoRow` (a custom
 * row, a note, a list) always shows, where it was written.
 */
export const FactCard = forwardRef<HTMLDivElement, FactCardProps>(
  ({ title, icon, children, className, ...props }, ref) => {
    const [showEmpty, setShowEmpty] = useState(false);
    const rows = flattenChildren(children);
    const shown = rows.filter((c) => !isEmptyInfoRow(c));
    const empty = rows.filter(isEmptyInfoRow);
    const n = empty.length;
    const noun = n === 1 ? "empty field" : "empty fields";
    const testId = (props as { "data-testid"?: string })["data-testid"];
    return (
      <Card
        ref={ref}
        surface="solid"
        padding="md"
        className={cn("flex flex-col", className)}
        {...props}
      >
        <div className="flex items-center gap-2 pb-2">
          {icon ? (
            <span aria-hidden="true" className="text-[var(--color-brand-strong)]">
              {icon}
            </span>
          ) : null}
          <h3 className="text-[15px] font-medium text-[var(--color-text-strong)]">
            {title}
          </h3>
        </div>
        <div className="flex flex-col">
          {shown}
          {showEmpty ? empty : null}
        </div>
        {n > 0 ? (
          <button
            type="button"
            aria-expanded={showEmpty}
            data-testid={testId ? `${testId}-empty-toggle` : "fact-card-empty-toggle"}
            onClick={() => setShowEmpty((v) => !v)}
            className="mt-2 self-start rounded-[var(--radius-sm)] py-1 text-[13px] font-medium text-[var(--color-brand-strong)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
          >
            {showEmpty ? "Hide" : "Show"} {n} {noun}
          </button>
        ) : null}
      </Card>
    );
  },
);
FactCard.displayName = "FactCard";
